import { useEffect, useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, Car, AlertTriangle, CalendarClock, RefreshCw, Pencil, Loader2, CalendarDays, Ban, ArrowLeftRight, Search, AlertCircle } from "lucide-react";
import { IvpLogo } from "@/components/IvpLogo";
import { MobileBookingCard } from "@/components/MobileBookingCard";
import { AvailabilitySection } from "@/components/AvailabilitySection";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { MaintenanceTab } from "@/components/admin/MaintenanceTab";
import { VehicleManagementTab } from "@/components/admin/VehicleManagementTab";
import { ChargingCompletionModal } from "@/components/admin/ChargingCompletionModal";
import { UpdateBookingDialog } from "@/components/admin/UpdateBookingDialog";
import { TransferBookingDialog } from "@/components/admin/TransferBookingDialog";
import { ServiceDetailsTab } from "@/components/admin/ServiceDetailsTab";
import { CancelBookingDialog } from "@/components/CancelBookingDialog";
import { BookingsPagination } from "@/components/BookingsPagination";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDMY, todayISO } from "@/lib/date";
import {
  fetchAdminDashboard,
  fetchAvailability,
  fetchAdminVehicles,
  getAdminToken,
  clearAdminToken,
  adminCancelBooking,
} from "@/lib/api";

const STATUSES = ["Upcoming", "In Use", "Overdue", "Returned"];
const PAGE_SIZE = 20;

const toDateObj = (s) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtDuration = (min) => {
  if (min == null) return "—";
  if (min <= 0) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h > 0 ? `${h}h ` : ""}${m}m`;
};

const fmtTimeFromIso = (iso) => (iso ? iso.slice(11, 16) : "—");

const statusStyles = {
  Upcoming: "bg-slate-100 text-slate-600 ring-slate-200",
  "In Use": "bg-blue-50 text-blue-700 ring-blue-200",
  Overdue: "bg-red-50 text-red-600 ring-red-200",
  Returned: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Cancelled: "bg-rose-50 text-rose-600 ring-rose-200",
};

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#0F172A]">{value}</p>
        <p className="text-xs font-medium text-[#64748B]">{label}</p>
      </div>
    </div>
  </div>
);

export default function VehicleManagement() {
  const queryClient = useQueryClient();
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [stats, setStats] = useState({ in_use: 0, overdue: 0, today: 0 });
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [transferring, setTransferring] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [tab, setTab] = useState("dashboard");

  // Filters + server pagination
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fDate, setFDate] = useState("");
  const [fVehicle, setFVehicle] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [dateOpen, setDateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Availability overview (reused component)
  const [availDate, setAvailDate] = useState(todayISO);
  const [availability, setAvailability] = useState([]);
  const [availLoading, setAvailLoading] = useState(false);
  const [vehicleNames, setVehicleNames] = useState([]);

  const loadSeq = useRef(0);

  const loadVehicleNames = useCallback(async () => {
    try {
      const vs = await fetchAdminVehicles();
      setVehicleNames(vs.map((v) => v.name));
    } catch {
      /* ignore */
    }
  }, []);

  const loadAvailability = useCallback(async (d) => {
    setAvailLoading(true);
    try {
      setAvailability(await fetchAvailability(d));
    } catch {
      /* ignore */
    } finally {
      setAvailLoading(false);
    }
  }, []);

  const load = useCallback(async (p) => {
    const targetPage = p ?? page;
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(false);
    try {
      const params = { page: targetPage, page_size: PAGE_SIZE };
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (fVehicle !== "all") params.vehicle = fVehicle;
      if (fStatus !== "all") params.status = fStatus;
      if (fDate) params.date = fDate;
      const data = await fetchAdminDashboard(params);
      if (seq !== loadSeq.current) return; // stale response, ignore
      setStats(data.stats);
      setBookings(data.bookings);
      setTotal(data.total);
      setTotalPages(data.total_pages);
      if (data.page !== targetPage) setPage(data.page); // server clamped to a valid page
    } catch (err) {
      if (seq !== loadSeq.current) return;
      if (err?.response?.status === 401) {
        clearAdminToken();
        setAuthed(false);
        toast.error("Session expired, please sign in again");
      } else {
        setLoadError(true);
        toast.error("Failed to load bookings");
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [page, debouncedSearch, fVehicle, fStatus, fDate]);

  // Initial + availability/vehicle loads on auth
  useEffect(() => {
    if (authed) {
      loadAvailability(availDate);
      loadVehicleNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change resets to page 1
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, fVehicle, fStatus, fDate]);

  // Fetch the current page whenever auth / page / filters change
  useEffect(() => {
    if (authed) load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, page, debouncedSearch, fVehicle, fStatus, fDate]);

  const handleAvailDateChange = (d) => {
    setAvailDate(d);
    loadAvailability(d);
  };

  const handleLogout = () => {
    clearAdminToken();
    setAuthed(false);
  };

  const refreshAll = () => {
    load(page);
    loadAvailability(availDate);
  };

  const handleSaved = async () => {
    setEditing(null);
    await load(page);
    await loadAvailability(availDate);
    queryClient.invalidateQueries({ queryKey: ["availability"] });
  };

  const handleCancelConfirm = async ({ reason }) => {
    await adminCancelBooking(cancelling.booking_ref, { reason });
    toast.success(`Booking ${cancelling.booking_ref} cancelled`);
    setCancelling(null);
    await load(page);
    await loadAvailability(availDate);
    queryClient.invalidateQueries({ queryKey: ["availability"] });
  };

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  const selectCls =
    "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#0F172A] outline-none focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]";

  const renderActions = (b, p = "") =>
    b.status === "Cancelled" ? (
      <span className="text-xs font-semibold text-rose-500">Cancelled</span>
    ) : (
      <div className="flex items-center gap-2">
        <button
          onClick={() => setEditing(b)}
          className="flex items-center gap-1.5 rounded-lg bg-[#5C2D91] px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-[#4A2475]"
          data-testid={`${p}update-${b.booking_ref}`}
        >
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
        {(b.status === "Upcoming" || b.status === "In Use") && (
          <button
            onClick={() => setTransferring(b)}
            className="flex items-center gap-1.5 rounded-lg border border-[#5C2D91]/30 bg-[#5C2D91]/10 px-3 py-1.5 text-xs font-semibold text-[#5C2D91] transition-all hover:bg-[#5C2D91]/20"
            data-testid={`${p}transfer-${b.booking_ref}`}
          >
            <ArrowLeftRight className="h-3.5 w-3.5" /> Transfer
          </button>
        )}
        <button
          onClick={() => setCancelling(b)}
          className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-all hover:bg-red-100"
          data-testid={`${p}cancel-${b.booking_ref}`}
        >
          <Ban className="h-3.5 w-3.5" /> Cancel
        </button>
        {b.potential_delay && (
          <span title="Potential Delay" className="ml-auto" data-testid={`${p}delay-icon-${b.booking_ref}`}>
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          </span>
        )}
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-6 sm:px-6">
      {tab !== "maintenance" && (
        <ChargingCompletionModal
          onDeferToTable={() => setTab("maintenance")}
          onCompleted={() => {
            loadAvailability(availDate);
            load(page);
            loadVehicleNames();
            queryClient.invalidateQueries({ queryKey: ["availability"] });
            queryClient.invalidateQueries({ queryKey: ["vehicles"] });
          }}
        />
      )}
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-5">
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            <div className="shrink-0 max-sm:[&_img]:h-10"><IvpLogo /></div>
            <div className="min-w-0">
              <h1 className="truncate font-heading text-sm font-bold text-[#0F172A] sm:text-xl" data-testid="admin-title">Vehicle Management</h1>
              <p className="hidden text-xs text-[#64748B] sm:block">Fleet admin dashboard</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={refreshAll}
              aria-label="Refresh"
              className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-[#0F172A] transition-all hover:bg-slate-50 sm:min-h-0"
              data-testid="admin-refresh"
            >
              <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleLogout}
              aria-label="Logout"
              className="flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg bg-[#0F172A] px-3 py-2 text-sm font-semibold text-white transition-all hover:bg-[#1e293b] sm:min-h-0"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-1.5 rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm sm:flex sm:gap-2 [&>button]:min-h-[44px] [&>button]:px-2 sm:[&>button]:min-h-0 sm:[&>button]:px-4" data-testid="admin-tabs">
          <button
            onClick={() => setTab("dashboard")}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${tab === "dashboard" ? "bg-[#5C2D91] text-white" : "text-[#64748B] hover:bg-slate-50"}`}
            data-testid="tab-dashboard"
          >
            Dashboard
          </button>
          <button
            onClick={() => setTab("maintenance")}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${tab === "maintenance" ? "bg-[#5C2D91] text-white" : "text-[#64748B] hover:bg-slate-50"}`}
            data-testid="tab-maintenance"
          >
            Maintenance / Charging
          </button>
          <button
            onClick={() => setTab("vehicles")}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${tab === "vehicles" ? "bg-[#5C2D91] text-white" : "text-[#64748B] hover:bg-slate-50"}`}
            data-testid="tab-vehicles"
          >
            Vehicle Management
          </button>
          <button
            onClick={() => setTab("service")}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-all ${tab === "service" ? "bg-[#5C2D91] text-white" : "text-[#64748B] hover:bg-slate-50"}`}
            data-testid="tab-service"
          >
            Service Details
          </button>
        </div>

        {tab === "vehicles" ? (
          <VehicleManagementTab onChanged={() => { loadAvailability(availDate); loadVehicleNames(); queryClient.invalidateQueries({ queryKey: ["availability"] }); queryClient.invalidateQueries({ queryKey: ["vehicles"] }); }} />
        ) : tab === "service" ? (
          <ServiceDetailsTab onChanged={() => queryClient.invalidateQueries({ queryKey: ["maintenance-summary"] })} />
        ) : tab === "maintenance" ? (
          <MaintenanceTab onChanged={() => loadAvailability(availDate)} />
        ) : (
        <>
        {/* Stats */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard icon={Car} label="Vehicles currently in use" value={stats.in_use} color="bg-blue-50 text-blue-600" testId="stat-in-use" />
          <StatCard icon={AlertTriangle} label="Overdue vehicles" value={stats.overdue} color="bg-red-50 text-red-600" testId="stat-overdue" />
          <StatCard icon={CalendarClock} label="Today's bookings" value={stats.today} color="bg-[#5C2D91]/10 text-[#5C2D91]" testId="stat-today" />
        </div>

        {/* Availability Overview (reused, real Supabase data) */}
        <AvailabilitySection
          selectedDate={availDate}
          onDateChange={handleAvailDateChange}
          availability={availability}
          isLoading={availLoading}
        />

        {/* Bookings table */}
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-bookings-section">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-semibold text-[#0F172A]">All Bookings</h2>
            <div className="grid w-full grid-cols-3 gap-2 md:flex md:w-auto md:flex-wrap md:items-center [&>*]:min-w-0">
              <div className="relative col-span-3 md:col-span-1 md:w-56">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94a3b8]" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search ref, name, email"
                  className={`${selectCls} w-full pl-8`}
                  data-testid="filter-search"
                />
              </div>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button type="button" className={`${selectCls} flex w-full items-center gap-2 truncate md:w-auto`} data-testid="filter-date">
                    <CalendarDays className="h-4 w-4 text-[#5C2D91]" />
                    {fDate ? formatDMY(fDate) : "All dates"}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto rounded-xl p-0">
                  <Calendar
                    mode="single"
                    selected={toDateObj(fDate)}
                    onSelect={(d) => { if (d) { setFDate(toDateStr(d)); setDateOpen(false); } }}
                    classNames={{
                      day_selected: "bg-[#5C2D91] text-white hover:bg-[#4A2475] hover:text-white rounded-md",
                      day_today: "ring-2 ring-[#5C2D91] ring-offset-1 rounded-md font-bold text-[#5C2D91]",
                      day: "h-9 w-9 p-0 font-normal rounded-md text-[#0F172A] hover:bg-[#5C2D91]/10",
                    }}
                  />
                  <div className="border-t border-slate-100 p-2">
                    <button
                      onClick={() => { setFDate(""); setDateOpen(false); }}
                      className="w-full rounded-md px-3 py-1.5 text-sm font-medium text-[#64748B] hover:bg-slate-50"
                      data-testid="filter-date-clear"
                    >
                      Clear date
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
              <select value={fVehicle} onChange={(e) => setFVehicle(e.target.value)} className={`${selectCls} w-full md:w-auto`} data-testid="filter-vehicle">
                <option value="all">All vehicles</option>
                {vehicleNames.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={`${selectCls} w-full md:w-auto`} data-testid="filter-status">
                <option value="all">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {loading && bookings.length === 0 ? (
            <div className="flex items-center gap-2 py-8 text-sm text-[#64748B]" data-testid="admin-loading">
              <Loader2 className="h-4 w-4 animate-spin text-[#5C2D91]" /> Loading bookings…
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="admin-error">
              <AlertCircle className="h-8 w-8 text-red-500" />
              <p className="text-sm text-[#64748B]">Could not load bookings. Please try again.</p>
              <button onClick={() => load(page)} className="rounded-lg bg-[#5C2D91] px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-[#4A2475]" data-testid="admin-retry">
                Retry
              </button>
            </div>
          ) : bookings.length === 0 ? (
            <p className="py-8 text-center text-sm text-[#64748B]" data-testid="admin-empty">No bookings match the filters.</p>
          ) : (
            <div className={`relative transition-opacity ${loading ? "pointer-events-none opacity-60" : "opacity-100"}`}>
              {loading && (
                <div className="absolute right-0 top-0 flex items-center gap-1.5 text-xs font-medium text-[#5C2D91]" data-testid="admin-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…
                </div>
              )}
              <div className="space-y-3 md:hidden" data-testid="admin-bookings-cards">
                {bookings.map((b) => (
                  <MobileBookingCard key={b.booking_ref} b={b} statusCls={statusStyles[b.status]} showEmail fmtTime={fmtTimeFromIso} fmtDuration={fmtDuration} actions={renderActions(b, "m-")} testid={`card-${b.booking_ref}`} />
                ))}
              </div>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[1100px] text-left text-sm" data-testid="admin-bookings-table">
                  <thead>
                    <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-[#64748B]">
                      <th className="py-3 pr-4">Ref</th>
                      <th className="py-3 pr-4">User</th>
                      <th className="py-3 pr-4">Vehicle</th>
                      <th className="py-3 pr-4">Date</th>
                      <th className="py-3 pr-4">Start</th>
                      <th className="py-3 pr-4">Est. Return</th>
                      <th className="py-3 pr-4">Actual Return</th>
                      <th className="py-3 pr-4">Usage</th>
                      <th className="py-3 pr-4">Overdue</th>
                      <th className="py-3 pr-4">Battery</th>
                      <th className="py-3 pr-4">Status</th>
                      <th className="py-3 pr-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.booking_ref} className="border-b border-slate-100 last:border-0" data-testid={`row-${b.booking_ref}`}>
                        <td className="py-3 pr-4 font-mono font-semibold text-[#5C2D91]">{b.booking_ref}</td>
                        <td className="py-3 pr-4">
                          <div className="font-medium text-[#0F172A]">{b.employee_name}</div>
                          <div className="text-xs text-[#64748B]">{b.company_email}</div>
                        </td>
                        <td className="py-3 pr-4 text-[#0F172A]">{b.vehicle}</td>
                        <td className="py-3 pr-4 text-[#0F172A]">{formatDMY(b.booking_date)}</td>
                        <td className="py-3 pr-4 text-[#0F172A]">{b.start_time}</td>
                        <td className="py-3 pr-4 text-[#0F172A]">{b.return_time}</td>
                        <td className="py-3 pr-4 text-[#0F172A]">{fmtTimeFromIso(b.actual_return_datetime)}</td>
                        <td className="py-3 pr-4 text-[#0F172A]">{fmtDuration(b.actual_duration_minutes)}</td>
                        <td className="py-3 pr-4">
                          <span className={b.overdue_minutes > 0 ? "font-semibold text-red-600" : "text-[#64748B]"}>
                            {fmtDuration(b.overdue_minutes)}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-[#0F172A]">
                          {b.current_battery_percentage != null ? `${b.current_battery_percentage}%` : "—"}
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusStyles[b.status]}`} data-testid={`status-${b.booking_ref}`}>
                            {b.status}
                          </span>
                        </td>
                        <td className="py-3 pr-4">{renderActions(b)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <BookingsPagination
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
                testid="admin-pagination"
              />
            </div>
          )}
        </section>
        </>
        )}
      </div>

      {editing && (
        <UpdateBookingDialog booking={editing} onClose={() => setEditing(null)} onSaved={handleSaved} />
      )}
      {transferring && (
        <TransferBookingDialog
          booking={transferring}
          onClose={() => setTransferring(null)}
          onDone={async () => {
            setTransferring(null);
            await load(page);
            await loadAvailability(availDate);
            queryClient.invalidateQueries({ queryKey: ["availability"] });
            queryClient.invalidateQueries({ queryKey: ["vehicles"] });
          }}
        />
      )}
      {cancelling && (
        <CancelBookingDialog booking={cancelling} onConfirm={handleCancelConfirm} onClose={() => setCancelling(null)} />
      )}
    </div>
  );
}
